import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

interface UseAdminQueryOptions {
  /** Delay the fetch by this many ms after the last dependency change — for search inputs. */
  debounceMs?: number;
}

interface UseAdminQueryResult<T> {
  data: T | null;
  setData: Dispatch<SetStateAction<T | null>>;
  loading: boolean;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
}

/**
 * Shared fetch/loading/error/cancelled-effect logic for the admin pages —
 * each one previously hand-rolled this (two of them also re-implementing the
 * same 300ms search debounce). `setData`/`setError` are exposed so a page can
 * still patch state locally after a mutation (e.g. flipping one row's
 * `active` flag) without refetching the whole list.
 */
export function useAdminQuery<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
  options?: UseAdminQueryOptions
): UseAdminQueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    function run() {
      fetcher()
        .then((result) => {
          if (!cancelled) setData(result);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof Error ? err.message : "Something went wrong");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    if (options?.debounceMs) {
      const timeout = setTimeout(run, options.debounceMs);
      return () => {
        cancelled = true;
        clearTimeout(timeout);
      };
    }

    run();
    return () => {
      cancelled = true;
    };
    // deps is caller-controlled — each page passes exactly the values its
    // own fetcher closes over (e.g. [search, status]).
  }, deps);

  return { data, setData, loading, error, setError };
}
